import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListTableShell } from "@/components/ResourceShell";
import { ListToolbar } from "@/components/ListToolbar";
import { SpecPhaseBadge } from "@/components/StatusBadges";
import { SpecDialog } from "@/components/dialogs/SpecDialog";
import { AdminInviteDialog } from "@/components/AdminInviteDialog";
import { useSpecs } from "@/api/queries/provisioning";
import { useCustomers } from "@/api/queries/customers";
import { relativeTime } from "@/lib/relativeTime";
import type { AdminInviteIssue, Customer } from "@/types";

// #343 UC-8: alle Kundenspezifikationen — Soll (Version) neben Ist (Status aus der Zelle).
export function SpecsList() {
  const specs = useSpecs();
  const customers = useCustomers();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  // #343 (UC-9): Einladung des Erst-Admins aus der Antwort des Anlegens — genau einmal sichtbar.
  const [invite, setInvite] = useState<AdminInviteIssue | null>(null);
  const customerById = new Map<string, Customer>((customers.data ?? []).map((c) => [c.id, c]));

  const all = specs.data ?? [];
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return term ? all.filter((s) => s.customer.includes(term) || s.cell.includes(term) || (customerById.get(s.customerId)?.name.toLowerCase().includes(term) ?? false)) : all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, q, customers.data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text3">{filtered.length} von {all.length} Spezifikationen</div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-spec-button"><Plus className="h-4 w-4" /> Kunde ausrollen</Button>
      </div>
      <ListToolbar search={q} onSearch={setQ} placeholder="Kunde oder Zelle suchen…" />
      <ListTableShell isLoading={specs.isLoading} isError={specs.isError} errorMessage={specs.error?.message} isEmpty={filtered.length === 0} emptyMessage={all.length === 0 ? "Noch keine Spezifikationen" : "Keine Treffer"} emptyHint="Eine Spezifikation = ein Kunde in einer Zelle (Plan, Version, Werte)." onRetry={() => specs.refetch()}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kunde</TableHead>
              <TableHead>Zelle</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Soll / Ist</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Gemeldet</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((s) => (
              <TableRow key={s.id} data-clickable="true" onClick={() => navigate(`/customer-specs/${s.id}`)}>
                <TableCell className="font-medium">{s.customer}<span className="ml-2 text-xs text-text3">{customerById.get(s.customerId)?.name ?? ""}</span></TableCell>
                <TableCell className="text-text2">{s.cell}</TableCell>
                <TableCell>{s.plan.toUpperCase()}</TableCell>
                <TableCell className="mono text-xs">{s.imageTag}</TableCell>
                <TableCell className="tnum">v{s.version} / {s.lastStatus ? `v${s.lastStatus.portalVersion}` : "—"}</TableCell>
                <TableCell>
                  <span className="flex items-center gap-2">
                    <SpecPhaseBadge phase={s.lastStatus?.phase} removed={s.removed} />
                    {s.secrets.stale && <Badge variant="busy" data-testid="stale-secrets">Geheimnisse erneuern</Badge>}
                  </span>
                </TableCell>
                <TableCell className="text-text3">{s.lastStatusAt ? relativeTime(s.lastStatusAt) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>
      {createOpen && <SpecDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(created) => setInvite(created.adminInvite)} />}
      <AdminInviteDialog invite={invite} onClose={() => setInvite(null)} />
    </div>
  );
}
